import { useState } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ProtectedElement from '../../../auth/ProtectedElement';
import { useUser } from '../../../hooks/useUser';
import { sharePinRoles } from '../../../utils/authorization';
import { regeneratePin } from '../../../networkRequest/officialsService';

interface PinMenuProps {
  officialId: number;
  setError: React.Dispatch<React.SetStateAction<string>>;
  setSharePinDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUpdatedPin: React.Dispatch<React.SetStateAction<string | null>>;
  refreshData: () => Promise<void>;
}

export default function PinMenu({
  officialId,
  setError,
  setSharePinDialogOpen,
  setUpdatedPin,
  refreshData,
}: PinMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { user } = useUser();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  const sharePin = () => {
    setAnchorEl(null);
    setSharePinDialogOpen(true);
  }

  const handleRegeneratePin = async () => {
    if (user?.accessToken && officialId) {
      const { data, error } = await regeneratePin(user?.accessToken, officialId);
      if (data) {
        await refreshData();
        setUpdatedPin(data.pin ?? null);
        setSharePinDialogOpen(true);
      } else if (error) {
        setError(error)
      }
    }
    setAnchorEl(null);
  }

  return (
    <>
      <IconButton
        onClick={handleClick}
        data-testid='pin-menu-button'
        aria-label='PIN options menu'
        disableFocusRipple
        disableRipple
        sx={{ padding: 0 }}
      >
        <MoreVertIcon fontSize='small' />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        data-testid='pin-options-menu'
      >
        <MenuItem onClick={handleRegeneratePin}>Regenerate Verifier PIN</MenuItem>
        {user &&
          <ProtectedElement userProfile={user.profile} allowedRoles={sharePinRoles}>
            <MenuItem onClick={sharePin}>Share Verifier PIN</MenuItem>
          </ProtectedElement>
        }
      </Menu>
    </>
  );
}
